import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import i18n from 'i18next';
import querystring from 'query-string';
import { css } from '@emotion/core';
import TextField from '@material-ui/core/TextField';

import { getPasscodeVerify } from 'api/client';
import * as clientErrTypes from 'config/clientErrorTypes';
import { getPasscodeErrorType, getErrorTypeMessage } from 'config/passcode';
import { generateApiErrObj } from 'client/hooks/useClientErrorHandler';

import { updateShowPasswordQualifyStatus } from 'client/redux/modules/promoState';
import useScrollElementOnMount from 'client/hooks/useScrollElementOnMount';

import Popup from 'client/components/Popup';
import DialogPasscodeSuccess from 'client/components/dialogVerification/DialogPasscodeSuccess';
import { useReCaptcha } from 'client/hooks/useReCaptcha';
import DialogReCaptcha from 'components/Dialog/DialogReCaptcha';

export default function DialogEnterPasscode({ open, onClose = () => {}, onApiError = () => {} }) {
    const dispatch = useDispatch();
    const bindRef = useScrollElementOnMount();
    const { uuid: promoUUID } = useSelector(s => s.promo);
    const { enableReCaptcha } = useSelector(s => s.settingPromo);
    const [isRequesting, setIsRequesting] = useState(false);
    const [errorType, setErrorType] = useState('');
    const [passcode, setPasscode] = useState('');
    const [openSuccessDialog, setOpenSuccessDialog] = useState(false);
    const { isShowReCaptcha, setShowReCaptcha } = useReCaptcha();

    useEffect(() => {
        const { pcode = '' } = querystring.parse(window.location.search);
        setPasscode(open ? pcode : '');
        setErrorType('');
    }, [open]);

    const handleSubmitClick = () => {
        if (enableReCaptcha) setShowReCaptcha(true);
        else handleSubmit();
    };

    async function handleSubmit(token) {
        setIsRequesting(true);
        try {
            const resp = await getPasscodeVerify({ promoUUID, passcode, recaptchaToken: token });
            const {
                httpStatus,
                payload: { data }
            } = resp;
            if (httpStatus === 200) {
                setOpenSuccessDialog(true);
                setTimeout(() => {
                    onClose();
                    dispatch(
                        updateShowPasswordQualifyStatus({
                            hasUsablePasscode: Boolean(data?.remainingCounts),
                            passcode: data?.remainingCounts ? passcode : '',
                            passcodeRemainingCount: data?.remainingCounts
                        })
                    );
                }, 2000);
            } else {
                const errType = getPasscodeErrorType(resp);
                if (errType) {
                    setErrorType(errType);
                } else {
                    throw generateApiErrObj(resp, clientErrTypes.VERIFY_PASSCODE);
                }
            }
        } catch (error) {
            onApiError(error);
        } finally {
            setIsRequesting(false);
        }
    }
    function handleChange(e) {
        const {
            target: { value }
        } = e;
        setPasscode(value);
        setErrorType('');
    }

    return (
        <>
            <DialogPasscodeSuccess open={openSuccessDialog} />
            {isShowReCaptcha && (
                <DialogReCaptcha onOpen={setShowReCaptcha} onComplete={handleSubmit} />
            )}
            <Popup
                showClose
                isOpen={open && !openSuccessDialog}
                title={i18n.t('label.passcode.verify')}
                onClose={onClose}
                endButton={i18n.t('btn.submit')}
                endButtonDisable={isRequesting || !passcode.trim()}
                onConfirm={handleSubmitClick}
                disableBackdropClick
            >
                <div css={dialogContentStyle} ref={bindRef}>
                    <TextField
                        hiddenLabel
                        fullWidth
                        color="primary"
                        variant="outlined"
                        placeholder={i18n.t('placeholder.passcode')}
                        onChange={handleChange}
                        value={passcode}
                        error={Boolean(errorType)}
                        helperText={Boolean(errorType) && getErrorTypeMessage(errorType)}
                    />
                </div>
            </Popup>
        </>
    );
}

const dialogContentStyle = css`
    padding: 16px;
`;
